import { onUpdated } from "vue";
import { storeToRefs } from "pinia";
import { useAnalyticsStore } from "@/store/analytics";
import { useAppStore } from "@/store/app";
import { useBiddingStore } from "@/store/bidding";
import { useModuleStore } from "@/store/module";
import { usePriceStore } from "@/store/price";
import { useProductAdStore } from "@/store/productAd";
import { useRoute, useRouter } from "vue-router";
import { useShopStore } from "@/store/shop";
import { useTicketStore } from "@/store/ticket";
import { useUserStore } from "@/store/user";

const useHelpers = () => {
    const shopStore = useShopStore();
    const { shops, selectedShopId, selectedShop, loadingShopStores } = storeToRefs(shopStore);
    const appStore = useAppStore();
    const { dateFrom, dateTo } = storeToRefs(appStore);
    const analyticsStore = useAnalyticsStore();
    const {
        abortActions,
        categoriesBiddingsLoading,
        categoriesBiddingsOrderBy,
        categoriesBiddingsSortBy,
        categoriesLoading,
        categoriesPage,
        categoriesPerPage,
        categoriesSortBy,
        categoriesSortOrder,
        clickClicksLoading,
        clickLoading,
        clicksPage,
        clicksPerPage,
        currentSelectedId,
        currentSelectedType,
        dailyBidMetricsLoading,
        dailyMetricsLoading,
        dailyTopProductsMetricsLoading,
        devicesLoading,
        distributionLoading,
        liquidityLoading,
        marketBrandsPage,
        marketBrandsPerPage,
        marketCategoriesPage,
        marketCategoriesPerPage,
        marketProductsPage,
        marketProductsPerPage,
        plotItems,
        selectedGroupBy,
        topCategoriesClicksLoading,
        topCategoriesLoading,
        topCategoriesPage,
        topCategoriesPerPage,
        topProductsLoading,
        topProductsPage,
        topProductsPerPage,
    } = storeToRefs(analyticsStore);
    const route = useRoute();
    const router = useRouter();
    const userStore = useUserStore();
    const { user } = storeToRefs(userStore);
    const biddingStore = useBiddingStore();
    const { categoryBiddingDataLoading } = storeToRefs(biddingStore);
    const priceStore = usePriceStore();
    const { pricesLoading } = storeToRefs(priceStore);
    const moduleStore = useModuleStore();
    const ticketStore = useTicketStore();
    const { ticketListLoading } = storeToRefs(ticketStore);
    const productAdStore = useProductAdStore();
    const {
        productAdsLoading,
        productAdInsightsLoading,
        productAdInsightsCategoriesLoading,
        productAdBoostLoading,
    } = storeToRefs(productAdStore);

    onUpdated(() => {
        analyticsStore.$onAction(({ name }) => {
            if (name in abortActions.value) {
                abortActions.value[name].abort();
            }

            abortActions.value[name] = new AbortController();
        });
    });

    const growthPercentage = (clicks, previousClicks) => {
        const current = clicks ? clicks : 0;
        const previous = previousClicks ? previousClicks : 0;

        return (
            (clicks > previousClicks ? "" : "-") +
            (Math.abs(current - previous) / current ? ((Math.abs(current - previous) / current) * 100).toFixed(2) : 0)
        );
    };

    const fetchShops = async () => {
        if (selectedShop.value === null && user.value.level >= 3) {
            await shopStore.fetchShops();

            selectedShop.value =
                selectedShopId.value !== null
                    ? shops.value.find((shop) => shop.id === selectedShopId.value)
                    : shops.value[0];
        }
    };

    const fetchClicksDataByReport = async () => {
        if (selectedShop.value && user.value.level >= 3) {
            const params = {
                shopId: selectedShop.value.id,
                data: {
                    date_from: dateFrom.value,
                    date_to: dateTo.value,
                    group_by: selectedGroupBy.value.code,
                    plot_items: plotItems.value.length > 0 ? plotItems.value : undefined,
                },
            };

            if (route.name === "Dashboard") {
                dailyMetricsLoading.value = true;
                await analyticsStore.fetchGraphDataV2(params);
                dailyMetricsLoading.value = false;
            }

            if (route.name === "ClickAnalytics") {
                clickClicksLoading.value = true;
                await analyticsStore.fetchClicksPerDayAnalytics(params);
                await analyticsStore.fetchCostPerClickAveragePerDayAnalytics(params);
                await analyticsStore.fetchNumberOfProductsAnalytics(params);
                clickClicksLoading.value = false;
            }

            if (route.name === "DashboardProducts") {
                dailyTopProductsMetricsLoading.value = true;

                await analyticsStore.fetchTopProductsDailyMetrics({
                    shopId: params.shopId,
                    data: {
                        ...params.data,
                        category_type: currentSelectedType.value ? `l${currentSelectedType.value}` : undefined,
                        category_id: currentSelectedId.value,
                    },
                });

                dailyTopProductsMetricsLoading.value = false;
            }

            if (route.name === "DashboardCategory") {
                topCategoriesClicksLoading.value = true;
                await analyticsStore.fetchTopCategoriesClicksAnalytics({
                    shopId: params.shopId,
                    data: {
                        ...params.data,
                        category_type: currentSelectedType.value ? `l${currentSelectedType.value}` : undefined,
                        category_id: currentSelectedId.value,
                    },
                });
                topCategoriesClicksLoading.value = false;
            }

            if (route.name === "DashboardCategoryV2") {
                dailyMetricsLoading.value = true;
                await analyticsStore.fetchDailyMetrics({
                    shopId: params.shopId,
                    data: {
                        ...params.data,
                    },
                });
                dailyMetricsLoading.value = false;
            }

            if (route.name === "DashboardDevices") {
                await analyticsStore.fetchDevicesPerDayAnalytics(params);
            }

            if (route.name === "DashboardActivities") {
                await analyticsStore.fetchDayAndHourAnalytics(params);
            }

            if (route.name === "DashboardLiquidity") {
                liquidityLoading.value = true;
                await analyticsStore.fetchLiquidityAnalytics(params);
                liquidityLoading.value = false;
            }

            if (route.name === "BiddingStatistics") {
                dailyBidMetricsLoading.value = true;
                await analyticsStore.fetchDailyBidMetrics(params);
                dailyBidMetricsLoading.value = false;
            }

            if (route.name === "ProductAdInsights") {
                productAdInsightsLoading.value = true;

                await productAdStore.fetchInsights({
                    shopId: params.shopId,
                    ...params,
                });

                productAdInsightsLoading.value = false;
            }

            if (route.name === "DashboardDistribution") {
                distributionLoading.value = true;
                await analyticsStore.fetchOffersCategoriesDistribution(params.shopId);
                await analyticsStore.fetchClicksPositionsDistribution(params);
                distributionLoading.value = false;
            }

            if (route.name === "Module") {
                await moduleStore.fetchGraphData(params);
            }

            router.push({ name: route.name, query: { shop_id: params.shopId, ...params.data } });
        }
    };

    const fetchTableDataByReport = async () => {
        if (selectedShop.value && user.value.level >= 3) {
            const params = {
                shopId: selectedShop.value.id,
                data: { date_from: dateFrom.value, date_to: dateTo.value, group_by: selectedGroupBy.value.code },
            };

            if (route.name === "Dashboard") {
                categoriesLoading.value = true;
                topCategoriesLoading.value = true;
                topProductsLoading.value = true;
                devicesLoading.value = true;

                await Promise.all([
                    analyticsStore.fetchNumberOfProductsAnalytics({ shopId: params.shopId }),
                    analyticsStore.fetchAvgProductPrice(params),
                    analyticsStore.fetchCategoriesAnalytics({
                        shopId: params.shopId,
                        data: {
                            ...params.data,
                            page: categoriesPage.value,
                            per_page: categoriesPerPage.value,
                            sort_by: categoriesSortBy.value,
                            order_by: categoriesSortOrder.value,
                        },
                    }),
                ]);

                await Promise.all([
                    analyticsStore.fetchTopProductsAnalytics(params),
                    analyticsStore.fetchTopCategoriesAnalytics(params),
                    analyticsStore.fetchDevicesAnalytics(params),
                    analyticsStore.fetchDayAndHourAnalytics(params),
                ]);

                categoriesLoading.value = false;
                topCategoriesLoading.value = false;
                topProductsLoading.value = false;
                devicesLoading.value = false;
            }

            if (route.name === "ClickAnalytics") {
                clickLoading.value = true;
                await Promise.all([
                    analyticsStore.fetchClicksAnalytics({
                        shopId: params.shopId,
                        data: { ...params.data, page: clicksPage.value, per_page: clicksPerPage.value },
                    }),
                    analyticsStore.fetchCostPerClickPerDayAnalytics(params),
                ]);
                clickLoading.value = false;
            }

            if (route.name === "DashboardProducts") {
                topProductsLoading.value = true;
                await Promise.all([
                    analyticsStore.fetchTopCategoriesByLevel({ shopId: params.shopId, data: { ...params.data } }),
                    analyticsStore.fetchTopProductsAnalytics({
                        shopId: params.shopId,
                        data: {
                            ...params.data,
                            page: topProductsPage.value,
                            per_page: topProductsPerPage.value,
                            category_type: currentSelectedType.value ? `l${currentSelectedType.value}` : undefined,
                            category_id: currentSelectedId.value,
                        },
                    }),
                ]);
                topProductsLoading.value = false;
            }

            if (route.name === "DashboardCategory") {
                topCategoriesLoading.value = true;
                await Promise.all([
                    analyticsStore.fetchTopCategoriesByLevel({ shopId: params.shopId, data: { ...params.data } }),
                    analyticsStore.fetchTopCategoriesAnalytics({
                        shopId: params.shopId,
                        data: {
                            ...params.data,
                            page: topCategoriesPage.value,
                            per_page: topCategoriesPerPage.value,
                            category_type: currentSelectedType.value ? `l${currentSelectedType.value}` : undefined,
                            category_id: currentSelectedId.value,
                        },
                    }),
                ]);
                topCategoriesLoading.value = false;
            }

            if (route.name === "DashboardCategoryV2") {
                topCategoriesLoading.value = true;
                await Promise.all([
                    analyticsStore.fetchTopCategoriesAnalyticsV2({ shopId: params.shopId, data: { ...params.data } }),
                ]);
                topCategoriesLoading.value = false;
            }

            if (route.name === "DashboardDevices") {
                await analyticsStore.fetchDevicesAnalytics(params);
            }

            if (route.name === "DashboardActivities") {
                await analyticsStore.fetchDayAndHourPerDayAnalytics(params);
            }

            if (route.name === "CategoriesMarket") {
                await analyticsStore.fetchMarketCategoriesAnalytics({
                    shopId: params.shopId,
                    data: {
                        ...params.data,
                        page: marketCategoriesPage.value,
                        per_page: marketCategoriesPerPage.value,
                    },
                });
            }

            if (route.name === "BrandsMarket") {
                await analyticsStore.fetchMarketBrandsAnalytics({
                    shopId: params.shopId,
                    data: {
                        ...params.data,
                        page: marketBrandsPage.value,
                        per_page: marketBrandsPerPage.value,
                    },
                });
            }

            if (route.name === "ProductsMarket") {
                await analyticsStore.fetchMarketProductsAnalytics({
                    shopId: params.shopId,
                    data: {
                        ...params.data,
                        page: marketProductsPage.value,
                        per_page: marketProductsPerPage.value,
                    },
                });
            }

            if (route.name === "Bidding") {
                categoryBiddingDataLoading.value = true;
                await biddingStore.fetchCategoriesBidding({
                    shopId: params.shopId,
                });
                await biddingStore.fetchCompanyAssociates({
                    shopId: params.shopId,
                });
                categoryBiddingDataLoading.value = false;
            }

            if (route.name === "BiddingStatistics") {
                categoriesBiddingsLoading.value = true;
                await analyticsStore.fetchCategoriesBiddings({
                    shopId: params.shopId,
                    data: {
                        ...params.data,
                        sort_by: categoriesBiddingsSortBy.value,
                        order_by: categoriesBiddingsOrderBy.value,
                    },
                });
                categoriesBiddingsLoading.value = false;
            }

            if (route.name === "ProductAdSmart") {
                productAdsLoading.value = true;
                await productAdStore.fetchProductAds({
                    shopId: params.shopId,
                });
                productAdsLoading.value = false;
            }

            if (route.name === "ProductAdBoost") {
                productAdBoostLoading.value = true;
                await Promise.all([
                    productAdStore.fetchCampaigns({ shopId: params.shopId }),
                    productAdStore.fetchBoostProducts({ shopId: params.shopId }),
                ]);
                productAdBoostLoading.value = false;
            }

            if (route.name === "ProductAdInsights") {
                productAdInsightsCategoriesLoading.value = true;

                await productAdStore.fetchInsightsCategories({
                    shopId: params.shopId,
                    ...params,
                });

                productAdInsightsCategoriesLoading.value = false;
            }

            if (route.name === "ProductLowestPrices") {
                pricesLoading.value = true;
                await priceStore.fetchCheapestPrices(params);
                pricesLoading.value = false;
            }

            if (route.name === "ProductExpensivePrices") {
                pricesLoading.value = true;
                await priceStore.fetchMoreExpensivePrices(params);
                pricesLoading.value = false;
            }

            if (route.name === "ProductHighestPrices") {
                pricesLoading.value = true;
                await priceStore.fetchMostExpensivePrices(params);
                pricesLoading.value = false;
            }

            if (route.name === "Module") {
                await moduleStore.fetchModuleCategories({
                    shopId: params.shopId,
                    data: { ...params.data },
                });
            }

            router.push({ name: route.name, query: { shop_id: params.shopId, ...params.data } });
        }
    };

    const fetchData = async () => {
        if (selectedShop.value && user.value.level >= 3) {
            if (route.name === "Shops") {
                loadingShopStores.value = true;
                await shopStore.fetchShopStores(selectedShop.value.id);
                await shopStore.fetchCities(selectedShop.value.country_id);
                loadingShopStores.value = false;
            }

            if (route.name === "PriceList") {
                await userStore.fetchPriceList(selectedShop.value.id);
            }

            if (route.name === "TicketList") {
                ticketListLoading.value = true;
                await ticketStore.fetchTickets(selectedShop.value.id);
                ticketListLoading.value = false;
            }
        }
    };

    const productUrlFormat = (product) => {
        if (selectedShop.value.country_locale_code === "rs") {
            return "https://www.eponuda.com" + product.product_url_rs;
        }

        if (selectedShop.value.country_locale_code === "ba") {
            return "https://www.eponuda.ba" + product.product_url_ba;
        }

        if (selectedShop.value.country_locale_code === "si" || selectedShop.value.country_locale_code === "hr") {
            return (
                process.env.VUE_APP_FE_URL +
                selectedShop.value.country_locale_code +
                product[`product_url_${selectedShop.value.country_locale_code}`]
            );
        }
    };

    const productNameFormat = (product) => {
        return product[`product_name_${selectedShop.value.country_locale_code}`];
    };

    const productColumnNameFormat = () => {
        if (selectedShop.value) {
            return `product_name_${selectedShop.value.country_locale_code}`;
        }
    };

    const productCategoryNameFormat = (product, level) => {
        if (selectedShop.value) {
            return product[`${level}_name_${selectedShop.value.country_locale_code}`];
        }
    };

    const productCategoryColumnFormat = (level) => {
        if (selectedShop.value) {
            return `${level}_name_${selectedShop.value.country_locale_code}`;
        }
    };

    const categoryNameFormat = (category) => {
        return category[`name_${selectedShop.value.country_locale_code}`];
    };

    const categoryColumnNameFormat = () => {
        if (selectedShop.value) {
            return `name_${selectedShop.value.country_locale_code}`;
        }
    };

    const padTo2Digits = (num) => {
        return num.toString().padStart(2, "0");
    };

    const formatTimeStamp = (timestamp, noSeconds = false) => {
        let dateTime = new Date(timestamp);
        let timeArray = [padTo2Digits(dateTime.getHours()), padTo2Digits(dateTime.getMinutes())];

        if (!noSeconds) {
            timeArray.push(padTo2Digits(dateTime.getSeconds()));
        }

        return (
            [dateTime.getFullYear(), padTo2Digits(dateTime.getMonth() + 1), padTo2Digits(dateTime.getDate())].join(
                "-"
            ) +
            " " +
            timeArray.join(":")
        );
    };

    const formatDate = (date) => {
        let dateTime = new Date(date);

        return [dateTime.getFullYear(), padTo2Digits(dateTime.getMonth() + 1), padTo2Digits(dateTime.getDate())].join(
            "-"
        );
    };

    const formatClickCategoryName = (click, level) => {
        if (selectedShop.value) {
            return click[`category_${level}_${selectedShop.value.country_locale_code}`];
        }
    };

    const formatClickCategoryGroupName = (click) => {
        if (selectedShop.value) {
            return click[`category_group_${selectedShop.value.country_locale_code}`];
        }
    };

    const formatClickCategoryColumn = (level) => {
        if (selectedShop.value) {
            return `category_${level}_${selectedShop.value.country_locale_code}`;
        }
    };

    const formatClickCategoryGroupColumn = () => {
        if (selectedShop.value) {
            return `category_group_${selectedShop.value.country_locale_code}`;
        }
    };

    const formatPrice = (price, decimals = null) => {
        const formatted = parseFloat(price);
        return formatted.toLocaleString("de-DE", { minimumFractionDigits: decimals ? decimals : 2 });
    };

    const formatNumber = (number) => {
        const formatted = parseInt(number);
        return formatted.toLocaleString("de-DE");
    };

    const getCurrency = () => {
        if (selectedShop.value.country_locale_code === "rs") {
            return "RSD";
        }

        if (selectedShop.value.country_locale_code === "si") {
            return "€";
        }

        if (selectedShop.value.country_locale_code === "hr") {
            return "€";
        }

        if (selectedShop.value.country_locale_code === "ba") {
            return "KM";
        }
    };

    const getCurrencyText = () => {
        if (selectedShop.value.country_locale_code === "rs") {
            return "RSD";
        }

        if (selectedShop.value.country_locale_code === "si") {
            return "EUR";
        }

        if (selectedShop.value.country_locale_code === "hr") {
            return "EUR";
        }

        if (selectedShop.value.country_locale_code === "ba") {
            return "KM";
        }
    };

    const biddingCategoryNameFormat = (category, level) => {
        if (selectedShop.value) {
            return category[`${level}_name_${selectedShop.value.country_locale_code}`];
        }
    };

    const addRemovePlotItems = (item) => {
        if (typeof item == "object") {
            if (plotItems.value.find((v) => v.plot_id === item.plot_id && v.plot_type === item.plot_type)) {
                plotItems.value.splice(
                    plotItems.value.findIndex((v) => v.plot_id === item.plot_id && v.plot_type === item.plot_type),
                    1
                );
            } else {
                if (plotItems.value.length >= 5) {
                    return false;
                } else {
                    plotItems.value.push(item);
                }
            }
        } else {
            if (plotItems.value.find((v) => v === item)) {
                plotItems.value.splice(plotItems.value.indexOf(item), 1);
            } else {
                if (plotItems.value.length >= 5) {
                    return false;
                } else {
                    plotItems.value.push(item);
                }
            }
        }

        return true;
    };

    const resetPlotItems = () => {
        plotItems.value = [];
    };

    const createUserPageLog = () => {
        appStore.createUserPageLog();
    };

    return {
        addRemovePlotItems,
        biddingCategoryNameFormat,
        categoryColumnNameFormat,
        categoryNameFormat,
        createUserPageLog,
        fetchClicksDataByReport,
        fetchData,
        fetchShops,
        fetchTableDataByReport,
        formatClickCategoryColumn,
        formatClickCategoryGroupColumn,
        formatClickCategoryGroupName,
        formatClickCategoryName,
        formatDate,
        formatNumber,
        formatPrice,
        formatTimeStamp,
        getCurrency,
        getCurrencyText,
        growthPercentage,
        plotItems,
        productCategoryColumnFormat,
        productCategoryNameFormat,
        productColumnNameFormat,
        productNameFormat,
        productUrlFormat,
        resetPlotItems,
    };
};

export default useHelpers;
